import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, Download, ExternalLink, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { api, fileUrl, fmtDate } from "../lib/api";
import { Card, PageHeader } from "../components/pm/bits";
import { Input } from "../components/ui/input";

export default function Documents() {
  const { documents, openDialog, refresh } = useApp();
  const [q, setQ] = useState("");
  const list = documents.filter((d) => `${d.name} ${d.description} ${d.project} ${d.category}`.toLowerCase().includes(q.toLowerCase()));
  const del = async (d) => {
    if (!window.confirm(`Delete ${d.name}?`)) return;
    await api.delete(`/documents/${d.id}`);
    toast.success("Document deleted");
    refresh();
  };
  return (
    <div className="mx-auto max-w-7xl animate-fade-in-up">
      <PageHeader title="Documents" subtitle={`${documents.length} live documents`}>
        <button onClick={() => openDialog("document")} className="flex h-9 items-center gap-1.5 rounded-lg bg-[#00573f] px-3 text-sm font-semibold text-white hover:bg-[#004330]" data-testid="add-document-btn">
          <Plus className="h-4 w-4" /> Add Document
        </button>
      </PageHeader>
      <div className="relative mb-5 w-full max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid="documents-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents..." className="h-9 bg-white pl-9" />
      </div>
      <Card>
        {list.length === 0 ? (
          <p className="p-10 text-center text-sm text-slate-400">No documents found.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {list.map((d) => (
              <div key={d.id} className="group flex items-center gap-4 p-4 hover:bg-[#fbf8f1]" data-testid={`doc-row-${d.id}`}>
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#f5efe3] text-[#a08450]"><FileText className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-900">{d.name}</p>
                  <p className="truncate text-xs text-slate-500">{d.description || d.fileName}</p>
                  <p className="mt-0.5 truncate text-xs text-slate-400">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{d.category}</span>
                    {d.project && <> · <Link to={`/projects/${d.projectId}`} className="text-[#00573f] hover:underline">{d.project}</Link></>}
                    {d.uploadedByName && ` · ${d.uploadedByName}`} · {fmtDate(d.createdAt)}
                  </p>
                </div>
                {d.storagePath && (
                  <a href={fileUrl(d.id)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-[#00573f] hover:text-[#00573f]" data-testid={`doc-download-${d.id}`}>
                    <Download className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Open</span>
                  </a>
                )}
                {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="rounded-md p-2 text-slate-500 hover:bg-slate-100"><ExternalLink className="h-4 w-4" /></a>}
                <button onClick={() => del(d)} className="rounded-md p-2 text-slate-300 hover:bg-red-50 hover:text-red-600" title="Delete" data-testid={`doc-delete-${d.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
